// import React, { useState, useEffect } from 'react';
// import { Box, Button, Group, Title, Modal, Text } from '@mantine/core';
// import { TextField, Autocomplete } from '@mui/material';

// import { MdmSetUpDashboard } from './mdmSetUpDashboard';
// import { useNavigate } from 'react-router-dom';
// import { getMetaDataConfig } from '../../services/uiConfigService';

// interface MdmRoleSetupProps {
//   importOptions?: Array<{ label: string; value: string }>;
// }

// const DEFAULT_ROLES = ['admin', 'manager', 'viewer'];

// const DEFAULT_IMPORT_OPTIONS = [
//   { label: 'Import existing admin configuration', value: 'true' },
//   { label: 'Start fresh with base configuration', value: 'false' }
// ];

// export const MdmRoleSetup: React.FC<MdmRoleSetupProps> = ({ 
//   importOptions = DEFAULT_IMPORT_OPTIONS 
// }) => {
//   const [roles, setRoles] = useState<string[]>(DEFAULT_ROLES);
//   const [selectedRole, setSelectedRole] = useState<string>('');
//   const [started, setStarted] = useState<boolean>(false);
//   const [importFromBase, setImportFromBase] = useState<string>('true');
//   const [loading, setLoading] = useState<boolean>(true);

//   const navigate = useNavigate();

//   useEffect(() => {
//     const fetchRoles = async () => {
//       try {
//         const rolesFromConfig = await getMetaDataConfig("clientConfig", "portal_configuration");
//         const rolesPresentConfig = rolesFromConfig?.domainValues?.find((option: {name: string}) => {
//           return option.name === "mdmSetupRoles"
//         });
//         if (rolesPresentConfig?.value && Array.isArray(rolesPresentConfig.value)) {
//           const roleLabels = rolesPresentConfig.value.map((role: {id: string, label: string}) => role.label);
//           setRoles(roleLabels);
//           if (roleLabels.length > 0) {
//             setSelectedRole(roleLabels[0]);
//           }
//         } else {
//           setRoles(DEFAULT_ROLES);
//           setSelectedRole(DEFAULT_ROLES[0]);
//         }
//       } catch (error) {
//         console.error('Error fetching roles config:', error);
//         setRoles(DEFAULT_ROLES);
//         setSelectedRole(DEFAULT_ROLES[0]);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchRoles();
//   }, []);

  
//   const handleClose = () => {
//     navigate('/dashboard/adminPanel');
//   };

//   // Show modal for role selection here
//   if (!started) {
//     return (
//       <Modal
//         opened={true}
//         onClose={handleClose}
//         title="Role-based MDM Setup"
//         centered
//         size="md"
//         closeOnClickOutside={false}
//         closeOnEscape={false}
//       >
//         <Box style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '8px' }}>
//           <Text size="sm" style={{ color: '#666' }}>
//             Choose a role and configuration import option to set up your MDM.
//           </Text>

//           <Autocomplete
//             options={roles}
//             value={selectedRole}
//             onChange={(event, newValue) => setSelectedRole(newValue || '')}
//             disabled={loading}
//             renderInput={(params) => (
//               <TextField
//                 {...params}
//                 label="Select Role"
//                 placeholder={loading ? "Loading roles..." : "Choose role"}
//                 size="small"
//               />
//             )}
//             fullWidth
//           />

//           <Autocomplete
//             options={importOptions}
//             value={importOptions.find(opt => opt.value === importFromBase) || importOptions[0]}
//             onChange={(event, newValue) => setImportFromBase(newValue?.value || 'true')}
//             getOptionLabel={(option) => option.label}
//             isOptionEqualToValue={(option, value) => option.value === value.value}
//             disabled={loading}
//             renderInput={(params) => (
//               <TextField
//                 {...params}
//                 label="Configuration Import"
//                 placeholder="Choose import option"
//                 size="small"
//               />
//             )}
//             fullWidth
//           />

//           <Group position="right" style={{ marginTop: '8px' }}>
//             <Button
//               onClick={() => setStarted(true)}
//               disabled={!selectedRole || loading}
//               style={{
//                 backgroundColor: (!selectedRole || loading) ? '#ccc' : '#237C9D',
//                 color: '#fff',
//                 textTransform: 'none'
//               }}
//             >
//               {loading ? 'Loading...' : 'Start Setup'}
//             </Button>
//           </Group>
//         </Box>
//       </Modal>
//     );
//   }

//   // Show dashboard with selected configuration
//   const domainTypePrefix = selectedRole ? selectedRole.toLowerCase() : undefined;
//   const shouldImportFromBase = importFromBase === 'true';

//   return (
//     <MdmSetUpDashboard domainTypePrefix={domainTypePrefix} importFromBase={shouldImportFromBase} />
//   );
// };

// export default MdmRoleSetup;


import React, { useState, useEffect } from 'react';
import { Box, Button, Group, Title, Modal, Text } from '@mantine/core';
import { TextField, Autocomplete } from '@mui/material';

import { MdmSetUpDashboard } from './mdmSetUpDashboard';
import { useNavigate } from 'react-router-dom';
import { getMetaDataConfig } from '../../services/uiConfigService';
import { openPopup } from '../../stateManagement/actions/popupActions';

interface MdmRoleSetupProps {
  importOptions?: Array<{ label: string; value: string }>;
}

const DEFAULT_ROLES = ['admin'];

const BASE_IMPORT_OPTION = { 
  label: 'Start fresh with base configuration', 
  value: 'base' 
};

export const MdmRoleSetup: React.FC<MdmRoleSetupProps> = ({ 
  importOptions 
}) => {
  const [roles, setRoles] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [started, setStarted] = useState<boolean>(false);
  const [importFromBase, setImportFromBase] = useState<string>('base');
  const [loading, setLoading] = useState<boolean>(true);
  const [dynamicImportOptions, setDynamicImportOptions] = useState<Array<{ label: string; value: string }>>([BASE_IMPORT_OPTION]);

  const navigate = useNavigate();

  // Fetch available roles
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const rolesFromConfig = await getMetaDataConfig("clientConfig", "portal_configuration");
        const rolesPresentConfig = rolesFromConfig?.domainValues?.find((option: {name: string}) => {
          return option.name === "mdmSetupRoles"
        });
        if (rolesPresentConfig?.value && Array.isArray(rolesPresentConfig.value)) {
          const roleLabels = rolesPresentConfig.value.map((role: {id: string, label: string}) => role.label);
          setRoles(roleLabels);
          if (roleLabels.length > 0) {
            setSelectedRole(roleLabels[0]);
          }
        } else {
          setRoles(DEFAULT_ROLES);
          setSelectedRole(DEFAULT_ROLES[0]);
        }
      } catch (error) {
        console.error('Error fetching roles config:', error);
        setRoles(DEFAULT_ROLES);
        setSelectedRole(DEFAULT_ROLES[0]);
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, []);

  useEffect(() => {
    const checkExistingConfigurations = async () => {
      if (roles.length === 0) return;

      const availableOptions: Array<{ label: string; value: string }> = [BASE_IMPORT_OPTION];

      for (const role of roles) {
        const domainTypePrefix = role.toLowerCase();
        
        try {
          const [formConfig, genericConfig] = await Promise.all([
            getMetaDataConfig("clientConfig", `${domainTypePrefix}_form_configuration`),
            getMetaDataConfig("clientConfig", `${domainTypePrefix}_generic_mdm_configuration`)
          ]);

          if (formConfig || genericConfig) {
            availableOptions.push({
              label: `Import from existing ${role} configuration`,
              value: domainTypePrefix
            });
          }
        } catch (error) {
          openPopup("Error", "Something went wrong");
          navigate('/dashboard/adminPanel');
        }
      }

      setDynamicImportOptions(availableOptions);
    };

    checkExistingConfigurations();
  }, [roles]);

  const handleClose = () => {
    navigate('/dashboard/adminPanel');
  };

  const finalImportOptions = importOptions || dynamicImportOptions;

  // Show modal for role selection
  if (!started) {
    return (
      <Modal
        opened={true}
        onClose={handleClose}
        title="Role-based MDM Setup"
        centered
        size="md"
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <Box style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '8px' }}>
          <Text size="sm" style={{ color: '#666' }}>
            Choose a role and configuration import option to set up your MDM.
          </Text>

          <Autocomplete
            options={roles}
            value={selectedRole}
            onChange={(event, newValue) => setSelectedRole(newValue || '')}
            disabled={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Role"
                placeholder={loading ? "Loading roles..." : "Choose role"}
                size="small"
              />
            )}
            fullWidth
          />

          <Autocomplete
            options={finalImportOptions}
            value={finalImportOptions.find(opt => opt.value === importFromBase) || finalImportOptions[0]}
            onChange={(event, newValue) => setImportFromBase(newValue?.value || 'base')}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            disabled={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Configuration Import"
                placeholder="Choose import option"
                size="small"
              />
            )}
            fullWidth
          />

          <Group position="right" style={{ marginTop: '8px' }}>
            <Button
              onClick={() => setStarted(true)}
              disabled={!selectedRole || loading}
              style={{
                backgroundColor: (!selectedRole || loading) ? '#ccc' : '#237C9D',
                color: '#fff',
                textTransform: 'none'
              }}
            >
              {loading ? 'Loading...' : 'Start Setup'}
            </Button>
          </Group>
        </Box>
      </Modal>
    );
  }

  const domainTypePrefix = selectedRole ? selectedRole.toLowerCase() : undefined;
  const shouldImportFromBase = importFromBase === 'base';
  const importFromRole = shouldImportFromBase ? undefined : importFromBase;

  return (
    <MdmSetUpDashboard 
      domainTypePrefix={domainTypePrefix} 
      importFromBase={shouldImportFromBase}
      importFromRole={importFromRole}
    />
  );
};

export default MdmRoleSetup;